import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ModulePageHeading from '../../components/ModulePageHeading';
import MailRecipientPreviewModal from '../../components/MailRecipientPreviewModal';
import { resolveSettingsNav } from '../../navigation/settingsNavigation';
import { buildMailPreviewParts } from '../../utils/mailPreview';

const COMPANY_ID = 'company-001';

export default function MailSettings() {
  const queryClient = useQueryClient();
  const [replyTo, setReplyTo] = useState('');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [templates, setTemplates] = useState({});
  const [activeTpl, setActiveTpl] = useState('issued_form_12a');
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['company-mail-settings', COMPANY_ID],
    queryFn: async () => (await api.get(`/companies/${COMPANY_ID}/mail-settings`)).data?.data,
  });

  useEffect(() => {
    if (!data) return;
    setReplyTo(data.reply_to || '');
    setNotifyEmail(data.notify_email || '');
    setHeaderText(data.header_text || '');
    setFooterText(data.footer_text || '');
    setTemplates(data.templates || {});
    const first = data.template_meta?.[0]?.id;
    if (first) setActiveTpl((prev) => prev || first);
  }, [data]);

  const meta = data?.template_meta || [];
  const placeholders = data?.placeholders || [];
  const smtp = data?.smtp || {};

  const activeMeta = useMemo(
    () => meta.find((m) => m.id === activeTpl) || meta[0],
    [meta, activeTpl]
  );
  const activeBody = templates[activeTpl] || { subject: '', body: '' };

  const inlinePreview = useMemo(
    () =>
      buildMailPreviewParts({
        subject: activeBody.subject || '',
        headerText,
        body: activeBody.body || '',
        footerText,
        filled: true,
      }),
    [activeBody.subject, activeBody.body, headerText, footerText]
  );

  const saveMutation = useMutation({
    mutationFn: async () =>
      (
        await api.put(`/companies/${COMPANY_ID}/mail-settings`, {
          reply_to: replyTo,
          notify_email: notifyEmail,
          header_text: headerText,
          footer_text: footerText,
          templates,
        })
      ).data?.data,
    onSuccess: () => {
      toast.success('Mail settings saved');
      queryClient.invalidateQueries({ queryKey: ['company-mail-settings', COMPANY_ID] });
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message || 'Save failed'),
  });

  const setTplField = (field, value) => {
    setTemplates((prev) => ({
      ...prev,
      [activeTpl]: {
        ...(prev[activeTpl] || { subject: '', body: '' }),
        [field]: value,
      },
    }));
  };

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolveSettingsNav}
        title="Mail settings"
        infoTitle="Mail settings"
        infoChildren={
          <div className="space-y-2 text-sm">
            <p>
              Configure email <strong>header</strong>, <strong>footer</strong>, and per-situation{' '}
              <strong>subject / body</strong> templates (e.g. Form 12A, S-RaD, Defect).
            </p>
            <p>
              Use <strong>Preview as recipient</strong> to see the filled message. Edit inside the
              preview to update these settings, then Save.
            </p>
            <p>
              Placeholders such as <code>{'{{building_name}}'}</code>, <code>{'{{ss_label}}'}</code>,{' '}
              <code>{'{{paperwork_label}}'}</code> are filled when sending Issued documents.
            </p>
            <p>
              <strong>Schedule mail</strong> holds the schedule request and the visit confirm. The request
              goes to the external company when the due-date window starts, and again from Scheduling.
              The confirm goes two working days before the booked date, and again from Scheduled.
            </p>
            <p>
              SMTP host / password stay in the server <code>.env</code> (Microsoft 365 etc.). This page
              does not store mailbox passwords.
            </p>
          </div>
        }
      />

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && (
        <p className="text-sm text-red-600">{error?.response?.data?.error || 'Failed to load'}</p>
      )}

      {data && (
        <div className="space-y-6">
          <div className="bg-card rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">SMTP status</h2>
            <p className="text-xs text-gray-500 mb-4">{smtp.note}</p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs text-gray-500">Configured</dt>
                <dd className={smtp.configured ? 'text-emerald-700 font-semibold' : 'text-amber-700 font-semibold'}>
                  {smtp.configured ? 'Yes — ready to send' : 'No — stub mode'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Host</dt>
                <dd>{smtp.host || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">From</dt>
                <dd className="break-all">{smtp.from || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Env notify</dt>
                <dd className="break-all">{smtp.notify_email_env || '—'}</dd>
              </div>
            </dl>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="text-xs font-medium text-gray-600">Reply-To (optional)</span>
                <input
                  type="email"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="admin@cbsl.co.nz"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs font-medium text-gray-600">Default notify email (optional override)</span>
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="admin@cbsl.co.nz"
                />
              </label>
            </div>
          </div>

          <div className="bg-card rounded-lg shadow p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Letterhead blocks</h2>
            <p className="text-xs text-gray-500">
              Applied above and below every template body when sending. Use the same placeholders.
            </p>
            <label className="block text-sm">
              <span className="text-xs font-medium text-gray-600">Header</span>
              <textarea
                rows={3}
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-medium text-gray-600">Footer</span>
              <textarea
                rows={3}
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
              />
            </label>
          </div>

          <div className="bg-card rounded-lg shadow p-6 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Situation templates</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Example: Building Centro · SS 4 Emergency Exit · Form 12A → subject/body filled from the
                  Form 12A template.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                <Eye className="w-4 h-4" />
                Preview as recipient
              </button>
            </div>
            {Array.from(
              meta.reduce((map, item) => {
                const group = item.group || 'Mail';
                if (!map.has(group)) map.set(group, []);
                map.get(group).push(item);
                return map;
              }, new Map())
            ).map(([group, items]) => (
              <div key={group} className="space-y-2">
                <p className="text-sm font-semibold text-gray-900">{group}</p>
                <div className="flex flex-wrap gap-2">
                  {items.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setActiveTpl(m.id)}
                      className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${
                        activeTpl === m.id
                          ? 'border-primary-500 bg-primary-50 text-primary-900'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {activeMeta && (
              <p className="text-xs text-gray-500">{activeMeta.hint}</p>
            )}
            <label className="block text-sm">
              <span className="text-xs font-medium text-gray-600">Subject</span>
              <input
                type="text"
                value={activeBody.subject || ''}
                onChange={(e) => setTplField('subject', e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-medium text-gray-600">Body</span>
              <textarea
                rows={10}
                value={activeBody.body || ''}
                onChange={(e) => setTplField('body', e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
              />
            </label>
            <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <p className="text-[11px] font-semibold text-slate-600 mb-1">Placeholders</p>
              <p className="text-[11px] text-slate-600 break-words">{placeholders.join(' ')}</p>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-slate-700">Quick recipient preview</p>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="text-xs font-semibold text-primary-700 hover:underline"
                >
                  Open full preview / edit
                </button>
              </div>
              <div className="px-3 py-3 space-y-2 text-sm">
                <p className="text-xs text-slate-500">
                  Subject:{' '}
                  <span className="font-semibold text-slate-800">{inlinePreview.subject || '—'}</span>
                </p>
                <pre className="whitespace-pre-wrap text-[13px] text-slate-700 font-sans leading-relaxed max-h-48 overflow-auto">
                  {inlinePreview.fullText || '(empty)'}
                </pre>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-800 hover:bg-slate-50"
            >
              <Eye className="w-4 h-4" />
              Preview as recipient
            </button>
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save mail settings'}
            </button>
          </div>
        </div>
      )}

      <MailRecipientPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        templateLabel={activeMeta?.label || activeTpl}
        fromAddress={smtp.from || ''}
        replyTo={replyTo}
        subject={activeBody.subject || ''}
        headerText={headerText}
        body={activeBody.body || ''}
        footerText={footerText}
        onChangeSubject={(v) => setTplField('subject', v)}
        onChangeHeader={setHeaderText}
        onChangeBody={(v) => setTplField('body', v)}
        onChangeFooter={setFooterText}
      />
    </div>
  );
}
